import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { formatDate, isDigit, isEmpty } from 'utils/stringUtil';
import { getRemainingDays, translateContactTypeEnum, translateStatusEnum } from 'utils/contactNoteUtil';
import { TableCell, TableRow, Tooltip } from '@mui/material';
import ContactNote from 'types/ContactNote';
import { getComparator, stableSort } from 'utils/sortUtil';
import { GlobalState } from 'reducers/stateTypes';
import { Order } from 'components/Common/DeprecatedTableHead';
import {
  setNoteDisplayDialog,
  setSelectedCnRow,
  setShownNote,
  toggleContactNoteDrawer,
} from 'reducers/contactNoteListReducer';
import { withStyles } from '@mui/styles';
import { JBColors, JBTheme } from '@juliusbaer-gcrm/gcrm-frontend-components';
import { DisplayDialogs } from 'views/ContactNotes/ContactNotesContainer';
import { EnvConfig } from 'config/envConfig';
import { ZfIcon } from '@zafire/react';

export const StyledTableCell = withStyles(() => ({
  body: {
    color: JBColors.black,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    padding: '8px 16px',
    maxWidth: '10vw',
    whiteSpace: EnvConfig.config.createContactNoteNewUiEnabled ? 'nowrap' : 'unset',
  },
}))(TableCell);

export enum ContactNoteStatus {
  SUBMITTED = 'SUBMITTED',
  EDITABLE = 'EDITABLE',
  CREATED = 'CREATED',
  INSUBMISSION = 'INSUBMISSION',
  EDITED = 'EDITED',
}

const ContactNoteRows = ({
  order,
  orderBy,
  page,
  rowsPerPage,
  filteredContactNotes,
}: {
  order: Order;
  orderBy: any;
  page: number;
  rowsPerPage: number;
  filteredContactNotes: ContactNote[];
}) => {
  const dispatch = useDispatch();
  const drawerOpen: boolean = useSelector((state: GlobalState) => state.contactNoteList.drawerOpen);
  const selectedRow: string | undefined = useSelector((state: GlobalState) => state.contactNoteList.selectedRow);

  const manageSelectedRow = (i: string) => {
    selectedRow === i ? dispatch(setSelectedCnRow(undefined)) : dispatch(setSelectedCnRow(i));
  };

  const contactTypeOptions = useSelector((state: GlobalState) => state.resources.contactTypeOptions);

  const contactTypeLabel = useCallback(
    (contactType: string) => {
      return contactTypeOptions.find((item) => item.value === contactType)?.label || '';
    },
    [contactTypeOptions],
  );

  const relatedPartnersLabel = useCallback((contactNote: ContactNote) => {
    let label = contactNote?.relatedPartners?.map((item) => item.fullName)?.join(', ') || '';
    if (contactNote?.relatedPartners.length === 0 && !contactNote?.otherPartner) {
      return '-';
    }
    if (contactNote?.relatedPartners?.length > 0 && contactNote?.otherPartner) {
      label += ', ';
    }
    if (contactNote.otherPartner) {
      label += 'Other';
    }
    return label;
  }, []);

  const tripIdLabel = useCallback((contactNote: ContactNote) => {
    return contactNote.tripId || '-';
  }, []);

  const relatedBRsLabel = useCallback((contactNote: ContactNote) => {
    if (contactNote?.relatedBRs?.length === 0) {
      return '-';
    }
    return contactNote?.relatedBRs
      ?.map((item) =>
        isDigit(item.masterNumber?.slice(0, 8))
          ? `${item?.masterNumber?.slice(0, 4)}.${item?.masterNumber?.slice(4)}`
          : item.masterNumber || `Prospecting BR`,
      )
      ?.join(', ');
  }, []);

  const renderCell = (contactNote: ContactNote, i: number) => {
    if (EnvConfig.config.createContactNoteNewUiEnabled) {
      return (
        <>
          <StyledTableCell>{contactNote.contactNoteId}</StyledTableCell>
          <StyledTableCell>{contactNote.startDateTime ? formatDate(contactNote.startDateTime) : ''}</StyledTableCell>
          <StyledTableCell>
            {relatedPartnersLabel(contactNote).length > 20 ? (
              <Tooltip title={relatedPartnersLabel(contactNote)} placement="bottom">
                <span
                  style={{
                    display: 'inline-block',
                    width: '100%',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {relatedPartnersLabel(contactNote)}
                </span>
              </Tooltip>
            ) : (
              relatedPartnersLabel(contactNote)
            )}
          </StyledTableCell>
          <StyledTableCell>
            {relatedBRsLabel(contactNote).length > 20 ? (
              <Tooltip title={relatedBRsLabel(contactNote)} placement="bottom">
                <span
                  style={{
                    display: 'inline-block',
                    width: '100%',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {relatedBRsLabel(contactNote)}
                </span>
              </Tooltip>
            ) : (
              relatedBRsLabel(contactNote)
            )}
          </StyledTableCell>
          <StyledTableCell>
            {contactNote?.subject?.length > 20 ? (
              <Tooltip title={contactNote.subject} placement="bottom">
                <span
                  style={{
                    display: 'inline-block',
                    width: '100%',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {contactNote.subject || '-'}
                </span>
              </Tooltip>
            ) : (
              `${contactNote.subject || '-'}`
            )}
          </StyledTableCell>
          <StyledTableCell>
            {contactNote?.notes?.length > 20 ? (
              <Tooltip title={contactNote.notes} placement="bottom">
                <span
                  style={{
                    display: 'inline-block',
                    width: '100%',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {contactNote.notes || '-'}
                </span>
              </Tooltip>
            ) : (
              `${contactNote.notes || '-'}`
            )}
          </StyledTableCell>

          <StyledTableCell>{contactTypeLabel(contactNote.contactType)}</StyledTableCell>
          <StyledTableCell>
            {contactNote.createdByFullName || 'N/A'} {contactNote.createdBy ? ` (${contactNote.createdBy})` : ''}
          </StyledTableCell>
          <StyledTableCell>{translateContactTypeEnum(contactNote.source)}</StyledTableCell>
          <StyledTableCell>
            {tripIdLabel(contactNote).length > 20 ? (
              <Tooltip title={tripIdLabel(contactNote)} placement="bottom">
                <span
                  style={{
                    display: 'inline-block',
                    width: '100%',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {tripIdLabel(contactNote)}
                </span>
              </Tooltip>
            ) : (
              tripIdLabel(contactNote)
            )}
          </StyledTableCell>
          <StyledTableCell>
            {contactNote?.attachments?.length > 0 ? <ZfIcon size="medium" name="attach_file" /> : '-'}
          </StyledTableCell>
          <StyledTableCell
            id={`contactNoteTable-status${i}`}
            style={
              getRemainingDays(contactNote.createdDateTime, 45) <= 3 &&
              contactNote.status !== ContactNoteStatus.SUBMITTED &&
              isEmpty(contactNote.tripId)
                ? { color: JBTheme.palette.error.main.toString() }
                : {}
            }
          >
            {translateStatusEnum(contactNote.status, contactNote.createdDateTime, contactNote.tripId)}
          </StyledTableCell>
        </>
      );
    }
    return (
      <>
        <StyledTableCell>{contactNote.startDateTime ? formatDate(contactNote.startDateTime) : ''}</StyledTableCell>
        <StyledTableCell>{contactNote.subject}</StyledTableCell>
        <StyledTableCell>{translateContactTypeEnum(contactNote.contactType)}</StyledTableCell>
        <StyledTableCell>{translateContactTypeEnum(contactNote.source)}</StyledTableCell>
        <StyledTableCell
          id={`contactNoteTable-status${i}`}
          style={
            getRemainingDays(contactNote.createdDateTime, 45) <= 3 &&
            contactNote.status !== ContactNoteStatus.SUBMITTED &&
            isEmpty(contactNote.tripId)
              ? { color: JBTheme.palette.error.main.toString() }
              : {}
          }
        >
          {translateStatusEnum(contactNote.status, contactNote.createdDateTime, contactNote.tripId)}
        </StyledTableCell>
        <StyledTableCell id={`contactNoteTable-contactNoteId${i}`}>{contactNote.contactNoteId}</StyledTableCell>
      </>
    );
  };

  const comparator = useMemo(() => {
    if (order === '') {
      return undefined;
    }
    return getComparator(order, orderBy);
  }, [order, orderBy]);

  return (
    <>
      {stableSort<ContactNote>(filteredContactNotes, comparator)
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
        .map((contactNote: ContactNote, i: number) => (
          <TableRow
            key={i}
            onClick={() => {
              dispatch(setNoteDisplayDialog(DisplayDialogs.DEFAULT_NOTE_VIEW));
              dispatch(toggleContactNoteDrawer(selectedRow === contactNote.id || !drawerOpen));
            }}
            onClickCapture={() => {
              dispatch(setShownNote(contactNote));
              manageSelectedRow(contactNote.id);
            }}
            hover={true}
            selected={selectedRow === contactNote.id && drawerOpen}
          >
            {renderCell(contactNote, i)}
          </TableRow>
        ))}
    </>
  );
};

export default ContactNoteRows;
